import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Video, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  RefreshCw,
  Play,
  Grid3X3,
  Pause,
  Volume2,
  VolumeX,
  Maximize
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface VideoItem {
  id: string;
  url: string;
  thumbnail: string;
  title: string;
  timestamp: string;
  duration?: string;
}

interface VideosSectionProps {
  videos: VideoItem[];
}

const VIDEOS_PER_PAGE = 20;

// Mock function to simulate loading more videos
const loadMoreVideos = async (page: number): Promise<VideoItem[]> => {
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  
  // Generate mock videos for demonstration
  const sampleVideos = [
    "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
    "https://www.w3schools.com/html/mov_bbb.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4"
  ];

  const newVideos: VideoItem[] = [];
  for (let i = 0; i < VIDEOS_PER_PAGE; i++) {
    const videoIndex = (page - 1) * VIDEOS_PER_PAGE + i + 1;
    const sampleUrl = sampleVideos[videoIndex % sampleVideos.length];
    newVideos.push({
      id: `video-${videoIndex}`,
      url: sampleUrl,
      thumbnail: `https://images.unsplash.com/photo-${1500000000 + videoIndex}?w=400&h=300&fit=crop`,
      title: `Vídeo da obra - Registro ${videoIndex}`,
      timestamp: new Date(Date.now() - videoIndex * 3600000).toISOString(),
      duration: `${Math.floor(Math.random() * 5) + 1}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`
    });
  }
  
  return newVideos;
};

export function VideosSection({ videos: initialVideos }: VideosSectionProps) {
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [allVideosModalOpen, setAllVideosModalOpen] = useState(false);
  const [allVideos, setAllVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Sort videos by timestamp (newest first)
  const sortedVideos = [...initialVideos].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const openPlayer = (index: number, videosArray = sortedVideos) => {
    setCurrentVideoIndex(index);
    setPlayerOpen(true);
    setIsPlaying(true);
  };

  const closePlayer = () => {
    setPlayerOpen(false);
    setIsPlaying(false);
  };

  const navigateVideo = (direction: 'prev' | 'next') => {
    const videosArray = allVideosModalOpen ? allVideos : sortedVideos;
    if (direction === 'prev') {
      setCurrentVideoIndex(prev => prev === 0 ? videosArray.length - 1 : prev - 1);
    } else {
      setCurrentVideoIndex(prev => prev === videosArray.length - 1 ? 0 : prev + 1);
    }
    setIsPlaying(true);
  };

  const openAllVideosModal = async () => {
    setAllVideosModalOpen(true);
    setError(false);
    
    if (allVideos.length === 0) {
      await loadInitialVideos();
    }
  };

  const loadInitialVideos = async () => {
    setLoading(true);
    try {
      const newVideos = await loadMoreVideos(1);
      setAllVideos([...initialVideos, ...newVideos]);
      setCurrentPage(2);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    try {
      const newVideos = await loadMoreVideos(currentPage);
      if (newVideos.length < VIDEOS_PER_PAGE) {
        setHasMore(false);
      }
      setAllVideos(prev => [...prev, ...newVideos]);
      setCurrentPage(prev => prev + 1);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!playerOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateVideo('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateVideo('next');
          break;
        case 'Escape':
          e.preventDefault();
          closePlayer();
          break;
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
      }
    };

    if (playerOpen) {
      document.addEventListener('keydown', handleKeyDown);
      playerRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [playerOpen]);

  // Infinite scroll for all videos modal
  useEffect(() => {
    if (!allVideosModalOpen || !gridRef.current) return;

    const handleScroll = () => {
      const element = gridRef.current;
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 200;

      if (isNearBottom && hasMore && !loading) {
        loadMore();
      }
    };

    const element = gridRef.current;
    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [allVideosModalOpen, hasMore, loading]);

  const currentVideosArray = allVideosModalOpen ? allVideos : sortedVideos;
  const currentVideo = currentVideosArray[currentVideoIndex];

  return (
    <>
      <Card data-section="videos" className="print-no-break print-flat">
        <CardHeader className="pt-1 pb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Video className="w-4 h-4" />
              Vídeos ({sortedVideos.length})
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={openAllVideosModal}
              className="text-xs"
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              Ver todos
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="py-1 print-expand">
          {sortedVideos.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-xs">Nenhum vídeo registrado para este dia</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedVideos.slice(0, 6).map((video, index) => (
                <div key={video.id} className="group cursor-pointer">
                  <div 
                    className="aspect-video bg-muted rounded-lg mb-1 flex items-center justify-center relative overflow-hidden border"
                    onClick={() => openPlayer(index)}
                  >
                    <img 
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                    {video.duration && (
                      <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1 rounded">
                        {video.duration}
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-xs line-clamp-2">{video.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(video.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </p>
                </div>
              ))}
              
              {sortedVideos.length > 6 && (
                <div 
                  className="aspect-video rounded-lg border-2 border-dashed bg-muted/30 flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={openAllVideosModal}
                >
                  <Grid3X3 className="w-6 h-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground text-center">
                    +{sortedVideos.length - 6} vídeos
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video Player Modal */}
      <Dialog open={playerOpen} onOpenChange={closePlayer}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none"
          ref={playerRef}
          tabIndex={-1}
        >
          <DialogTitle className="sr-only">Vídeos do RDO</DialogTitle>
          <DialogDescription className="sr-only">Visualização e gerenciamento dos vídeos do relatório.</DialogDescription>
          <div className="relative w-full h-full min-h-[60vh] flex flex-col">
            {currentVideo && (
              <>
                {/* Close button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 z-20 text-white hover:bg-white/20"
                  onClick={closePlayer}
                  aria-label="Fechar player"
                >
                  <X className="w-5 h-5" />
                </Button>

                {/* Navigation buttons */}
                {currentVideosArray.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20 z-20"
                      onClick={() => navigateVideo('prev')}
                      aria-label="Vídeo anterior"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20 z-20"
                      onClick={() => navigateVideo('next')}
                      aria-label="Próximo vídeo"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </Button>
                  </>
                )}

                {/* Video player */}
                <div className="flex-1 relative">
                  <video 
                    ref={videoRef}
                    src={currentVideo.url}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay={isPlaying}
                    muted={isMuted}
                  />
                </div>

                {/* Video info */}
                <div className="bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium mb-1">{currentVideo.title}</p>
                      <p className="text-xs text-gray-300">
                        {format(new Date(currentVideo.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      </p>
                      {currentVideosArray.length > 1 && (
                        <p className="text-xs text-gray-400 mt-1">
                          {currentVideoIndex + 1} de {currentVideosArray.length}
                        </p>
                      )}
                    </div>
                    
                    {/* Custom controls */}
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pausar" : "Reproduzir"}
                      >
                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                        onClick={toggleMute}
                        aria-label={isMuted ? "Ativar som" : "Silenciar"}
                      >
                        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                        onClick={toggleFullscreen}
                        aria-label="Tela cheia"
                      >
                        <Maximize className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* All Videos Modal */}
      <Dialog open={allVideosModalOpen} onOpenChange={setAllVideosModalOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full p-0">
          <DialogTitle className="sr-only">Vídeos do RDO</DialogTitle>
          <DialogDescription className="sr-only">Visualização e gerenciamento dos vídeos do relatório.</DialogDescription>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Todos os Vídeos</h3>
            </div>
            
            <div 
              ref={gridRef}
              className="max-h-[75vh] overflow-y-auto"
            >
              {error ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground mb-4">
                    Erro ao carregar os vídeos
                  </p>
                  <Button 
                    onClick={loadInitialVideos}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Tentar novamente
                  </Button>
                </div>
              ) : allVideos.length === 0 && !loading ? (
                <div className="text-center py-8">
                  <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">
                    Nenhum vídeo disponível
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allVideos.map((video, index) => (
                    <div 
                      key={video.id} 
                      className="group cursor-pointer"
                      onClick={() => {
                        setCurrentVideoIndex(index);
                        setPlayerOpen(true);
                      }}
                    >
                      <div className="aspect-video rounded-lg overflow-hidden border bg-muted relative">
                        <img 
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <Play className="w-8 h-8 text-white" />
                        </div>
                        {video.duration && (
                          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1 rounded">
                            {video.duration}
                          </div>
                        )}
                      </div>
                      <p className="text-xs mt-1 font-medium line-clamp-2">
                        {video.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(video.timestamp), "dd/MM HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  ))}
                  
                  {/* Loading skeletons */}
                  {loading && (
                    Array.from({ length: 6 }).map((_, i) => (
                      <div key={`skeleton-${i}`} className="space-y-2">
                        <Skeleton className="aspect-video rounded-lg" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {/* Load more indicator */}
              {hasMore && !loading && allVideos.length > 0 && (
                <div className="text-center py-4">
                  <Button 
                    onClick={loadMore}
                    variant="outline"
                    size="sm"
                  >
                    Carregar mais vídeos
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}